// ── useGameStatesPoll — value-dashboard's batched gameState poller ─
//
// Polls hydra's /api/analytics/game-states at 5s while mounted, scoped
// to the gameKeys it's handed. Result lands in `gameStatesStore` for
// any consumer to read.
//
// Chunks the key list at 50/request (hydra's hard cap). The dashboard
// rarely shows more than 30 games at once but the chunker keeps us
// safe for big slate days.
//
// Pauses on `document.hidden` so background tabs don't burn the
// endpoint. Resumes on visibility-change → triggers an immediate fetch
// so a returning tab sees fresh state instead of waiting up to 5s.

import { useEffect, useMemo, useRef } from 'react';
import {
  GAME_STATES_BATCH_CAP,
  getGameStates,
  hasAnalytics,
  type UnabatedGameState,
} from '../services/hydraAnalytics';
import { useGameStatesStore } from '../stores/gameStatesStore';
import { useMarketStore } from '../stores/marketStore';

const POLL_MS = 5_000;

export function useGameStatesPoll(gameKeys: string[]): void {
  // Stable identity for the key set so the effect doesn't churn on
  // every render. The actual fetch reads from `keysRef.current` so
  // out-of-band key set changes (e.g. user navigates dates) propagate
  // to the in-flight loop without restarting it.
  const keysRef = useRef<string[]>(gameKeys);
  keysRef.current = gameKeys;
  const stableKey = useMemo(
    () => gameKeys.slice().sort().join('|'),
    [gameKeys],
  );

  useEffect(() => {
    if (!hasAnalytics()) return;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const scheduleNext = () => {
      if (cancelled) return;
      timeoutId = setTimeout(tick, POLL_MS);
    };

    const tick = async () => {
      if (cancelled) return;
      if (document.hidden) {
        // Skip while tab is hidden; visibility-change handler triggers
        // the next fetch when we come back.
        scheduleNext();
        return;
      }
      const keys = keysRef.current;
      if (keys.length === 0) {
        scheduleNext();
        return;
      }
      try {
        // Chunk at 50 to respect hydra's cap. Most dashboards stay
        // under 50 so this is usually a single request.
        const chunks: string[][] = [];
        for (let i = 0; i < keys.length; i += GAME_STATES_BATCH_CAP) {
          chunks.push(keys.slice(i, i + GAME_STATES_BATCH_CAP));
        }
        const results = await Promise.all(chunks.map((c) => getGameStates(c)));
        if (cancelled) return;
        const merged: Record<string, UnabatedGameState | null> = {};
        let serverTs = 0;
        for (const r of results) {
          for (const [k, v] of Object.entries(r.states)) merged[k] = v;
          if (r.serverTs > serverTs) serverTs = r.serverTs;
        }
        useGameStatesStore.getState().setStates(merged, serverTs || Date.now());
        // Fan Unabated's eventStart into marketStore's canonical-key →
        // startTime cache. The store keeps per-source slots resolved by
        // precedence: post-start Unabated outranks the venues (live-delay
        // timing wins); pre-start it slots between kalshi and polymarket,
        // so venue schedule corrections still propagate. Each poll cycle
        // re-seeds the slot. Lets spreads/totals + niche-sport moneylines
        // pick up an event start for GTS expiry, countdown chips,
        // near-tip pulse, etc.
        const seed = useMarketStore.getState().seedEventStartTimeMsByKey;
        for (const [gameKey, state] of Object.entries(merged)) {
          if (!state || !state.eventStart) continue;
          const ms = Date.parse(state.eventStart);
          if (Number.isFinite(ms)) seed(gameKey, ms);
        }
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'gameStates fetch failed';
        useGameStatesStore.getState().setError(msg);
      }
      scheduleNext();
    };

    const onVisibilityChange = () => {
      if (!document.hidden && !cancelled) {
        if (timeoutId != null) clearTimeout(timeoutId);
        void tick();
      }
    };

    void tick();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      if (timeoutId != null) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
    // stableKey is the actual driver — keysRef just carries the live
    // set into the closure without restarting the loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableKey]);
}
